import { Typography } from 'app/components/ui';
import { LabeledSection } from './LabeledSection';
import { i18n } from 'app/utils/i18n';
import { DimensionsModal } from '../Modal/DimensionsModal';
import { useContext, useMemo, useState } from 'react';
import { dispatchMessage, EventType } from 'app/useToasts';
import {
  AvailableDimension,
  AvailableHypercube,
  AvailableMember,
  UUID,
  useConceptsByIds,
  useDimensionsForConcept,
  useFindOrCreateTagContext,
  useUpdateTag,
} from 'app/api/tag-queries';
import { TagItemShortcuts } from 'app/views/documents/tagging/TagItemShortcuts/TagItemShortcuts';
import { getTagLabel } from 'app/utils/tag-utils';
import { TagContext } from 'app/state/contexts/TagContext';

interface TagForDimensions {
  id: UUID;
  conceptId: UUID;
  taxonomyId: UUID;
  concept?: { name?: string };
  contextId?: UUID;
  context?: {
    id: UUID;
    periodStartDate?: Date | string | null;
    periodEndDate?: Date | string | null;
    periodInstant?: Date | string | null;
    periodForever?: boolean;
    scenariosExplicitMembers?: UUID[] | null;
    scenariosTypedMembers?: UUID[] | null;
    segmentExplicitMembers?: UUID[] | null;
    segmentTypedMembers?: UUID[] | null;
  } | null;
}

interface DimensionsSectionProps {
  tag: TagForDimensions;
  documentSuperId?: number;
  readOnly?: boolean;
}

type DimensionPair = { dimensionConceptId: UUID; memberConceptId: UUID };

function decodePairs(arr: UUID[] | null | undefined): DimensionPair[] {
  if (!arr || arr.length === 0) return [];
  const pairs: DimensionPair[] = [];
  const evenLength = arr.length - (arr.length % 2);
  for (let i = 0; i < evenLength; i += 2) {
    pairs.push({ dimensionConceptId: arr[i], memberConceptId: arr[i + 1] });
  }
  return pairs;
}

function encodePairs(pairs: DimensionPair[]): UUID[] {
  return pairs.flatMap((p) => [p.dimensionConceptId, p.memberConceptId]);
}

export const DimensionsSection = ({ tag, documentSuperId, readOnly }: DimensionsSectionProps) => {
  const { tagLanguage } = useContext(TagContext);
  const conceptName = tag.concept?.name ?? '';

  const { data: dimensionsResponse } = useDimensionsForConcept(documentSuperId, conceptName);
  const { mutate: findOrCreateTagContext, isLoading: isCreatingContext } = useFindOrCreateTagContext();
  const { mutate: updateTag, isLoading: isUpdatingTag } = useUpdateTag();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Build display list of (dimension, member) pairs with resolved labels
  const scenarioPairs = useMemo(() => decodePairs(tag.context?.scenariosExplicitMembers), [tag.context?.scenariosExplicitMembers]);
  const segmentPairs = useMemo(() => decodePairs(tag.context?.segmentExplicitMembers), [tag.context?.segmentExplicitMembers]);
  const allPairs = useMemo(() => [...scenarioPairs, ...segmentPairs], [scenarioPairs, segmentPairs]);

  // Lookup tables for label rendering. Keyed by concept UUID (what's stored
  // in the context arrays), with labels from the endpoint response.
  const labelByConceptId = useMemo(() => {
    const map = new Map<string, { labels: any; type: 'dimension' | 'member'; qname: string }>();
    if (!dimensionsResponse) return map;
    for (const hc of dimensionsResponse.hypercubes) {
      for (const dim of hc.dimensions) {
        if (dim.conceptId) map.set(dim.conceptId, { labels: dim.labels, type: 'dimension', qname: dim.id });
        for (const domain of dim.domains) {
          collectMembers(domain.members, map);
        }
      }
    }
    return map;
  }, [dimensionsResponse]);

  const unresolvedConceptIds = useMemo(() => {
    const all = new Set<string>();
    for (const p of allPairs) {
      all.add(p.dimensionConceptId);
      all.add(p.memberConceptId);
    }
    return Array.from(all).filter((id) => !labelByConceptId.has(id));
  }, [allPairs, labelByConceptId]);
  const { data: fallbackConcepts } = useConceptsByIds(unresolvedConceptIds);
  const fallbackLabelById = useMemo(() => {
    const map = new Map<string, { name: string; labels?: Record<string, Record<string, string>> }>();
    for (const c of fallbackConcepts ?? []) map.set(c.id, { name: c.name, labels: c.labels });
    return map;
  }, [fallbackConcepts]);

  const persistPairs = (newPairs: DimensionPair[], target: 'scenario' | 'segment') => {
    if (!tag.context || !tag.contextId) return;

    const encoded = encodePairs(newPairs);
    const ctx = tag.context;
    findOrCreateTagContext(
      {
        documentSuperId: documentSuperId ?? 0,
        taxonomyId: tag.taxonomyId,
        validateForConceptIds: [tag.conceptId],
        periodStartDate: ctx.periodStartDate as any,
        periodEndDate: ctx.periodEndDate as any,
        periodInstant: ctx.periodInstant as any,
        periodForever: ctx.periodForever,
        scenariosExplicitMembers: target === 'scenario' ? (encoded.length === 0 ? null : encoded) : ctx.scenariosExplicitMembers ?? null,
        scenariosTypedMembers: ctx.scenariosTypedMembers ?? null,
        segmentExplicitMembers: target === 'segment' ? (encoded.length === 0 ? null : encoded) : ctx.segmentExplicitMembers ?? null,
        segmentTypedMembers: ctx.segmentTypedMembers ?? null,
      },
      {
        onSuccess: (newCtx) => {
          if (newCtx.id !== tag.contextId) {
            updateTag({ id: tag.id, contextId: newCtx.id, documentSuperId });
          }
        },
      }
    );
  };

  const handleAdd = (dimension: AvailableDimension, member: AvailableMember, hypercube: AvailableHypercube) => {
    setIsAddModalOpen(false);
    if (!dimension.conceptId || !member.conceptId) {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: i18n('tag.dimensions.concept-not-found'),
        removeTime: 5000,
      });
      return;
    }
    const target = hypercube.contextElement;
    const currentPairs = target === 'scenario' ? scenarioPairs : segmentPairs;
    // Replace any existing pair on the same dimension with the new selection.
    const filtered = currentPairs.filter((p) => p.dimensionConceptId !== dimension.conceptId);
    const newPair: DimensionPair = { dimensionConceptId: dimension.conceptId, memberConceptId: member.conceptId };
    persistPairs([...filtered, newPair], target);
  };

  const handleTypedValueAdded = ({
    hypercube,
    dimension,
    typedValueId,
  }: {
    hypercube: AvailableHypercube;
    dimension: AvailableDimension;
    typedValueId: UUID;
    value: string;
  }) => {
    setIsAddModalOpen(false);
    if (!tag.context || !tag.contextId || !dimension.conceptId) return;
    const ctx = tag.context;
    const target = hypercube.contextElement;

    // Decode existing typed pairs (axisId, typedValueId) and replace any
    // entry on the same axis with the new typed-value UUID.
    const existing = (target === 'scenario' ? ctx.scenariosTypedMembers : ctx.segmentTypedMembers) ?? [];
    const next: UUID[] = [];
    const evenLen = existing.length - (existing.length % 2);
    let replaced = false;
    for (let i = 0; i < evenLen; i += 2) {
      if (existing[i] === dimension.conceptId) {
        next.push(existing[i], typedValueId);
        replaced = true;
      } else {
        next.push(existing[i], existing[i + 1]);
      }
    }
    if (!replaced) next.push(dimension.conceptId, typedValueId);

    findOrCreateTagContext(
      {
        documentSuperId: documentSuperId ?? 0,
        taxonomyId: tag.taxonomyId,
        validateForConceptIds: [tag.conceptId],
        periodStartDate: ctx.periodStartDate as any,
        periodEndDate: ctx.periodEndDate as any,
        periodInstant: ctx.periodInstant as any,
        periodForever: ctx.periodForever,
        scenariosExplicitMembers: ctx.scenariosExplicitMembers ?? null,
        scenariosTypedMembers: target === 'scenario' ? (next.length === 0 ? null : next) : ctx.scenariosTypedMembers ?? null,
        segmentExplicitMembers: ctx.segmentExplicitMembers ?? null,
        segmentTypedMembers: target === 'segment' ? (next.length === 0 ? null : next) : ctx.segmentTypedMembers ?? null,
      },
      {
        onSuccess: (newCtx) => {
          if (newCtx.id !== tag.contextId) {
            updateTag({ id: tag.id, contextId: newCtx.id, documentSuperId });
          }
        },
      }
    );
  };

  const handleRemove = (dimensionConceptId: UUID) => {
    const target: 'scenario' | 'segment' = scenarioPairs.some((p) => p.dimensionConceptId === dimensionConceptId) ? 'scenario' : 'segment';
    const source = target === 'scenario' ? scenarioPairs : segmentPairs;
    persistPairs(
      source.filter((p) => p.dimensionConceptId !== dimensionConceptId),
      target
    );
  };

  const busy = isCreatingContext || isUpdatingTag;

  // Resolve a UUID to a display label. Tries in order:
  //   1. The dimensions-for-concept response labels (best — has locale labels per dim/member)
  //   2. The concepts/batch fallback (always works if concept exists in DB)
  //   3. The QName from either source
  //   4. The raw UUID (last-resort so the UI never flashes blank)
  const resolveLabel = (conceptId: UUID): string => {
    const fromDim = labelByConceptId.get(conceptId);
    if (fromDim) {
      const flat = fromDim.labels as Record<string, string | undefined> | undefined;
      const locale = flat?.[tagLanguage] ?? flat?.en ?? flat?.fallback;
      if (locale) return locale.replace(/\s*\[.*?\]\s*/g, '').trim() || locale;
      return fromDim.qname;
    }
    const fallback = fallbackLabelById.get(conceptId);
    if (fallback) {
      if (fallback.labels) {
        const label = getTagLabel({ languages: fallback.labels as any, currentLang: tagLanguage });
        if (label) return label;
      }
      return fallback.name;
    }
    return conceptId;
  };

  const treeGroups = useMemo(() => {
    return allPairs.map((pair) => {
      const ancestors = dimensionsResponse ? findMemberAncestors(dimensionsResponse, pair.dimensionConceptId, pair.memberConceptId) : [];
      return { pair, ancestors };
    });
  }, [allPairs, dimensionsResponse]);

  return (
    <>
      <LabeledSection label={i18n('tag.dimensions.title')}>
        {allPairs.length === 0 && (
          <Typography variant="body-7" color="#8090a3">
            {i18n('tag.dimensions.none')}
          </Typography>
        )}

        {treeGroups.map(({ pair, ancestors }) => {
          const dimLabel = resolveLabel(pair.dimensionConceptId);
          const memberLabel = resolveLabel(pair.memberConceptId);
          return (
            <div key={pair.dimensionConceptId} className="dimension-tree">
              {/* Axis row (top, with axis icon + remove shortcut on the right) */}
              <div className="dimension-tree__node">
                <div className="dimension-tree__row">
                  <span className="dimension-tree__icon">
                    <i className="fa-regular fa-diagram-nested" />
                  </span>
                  <Typography variant="body-5" style={{ flexGrow: 1 }}>
                    {dimLabel}
                  </Typography>
                  {!readOnly && (
                    <TagItemShortcuts
                      actions={[
                        {
                          type: 'delete',
                          label: i18n('tag.dimensions.remove'),
                          onClick: () => handleRemove(pair.dimensionConceptId),
                        },
                      ]}
                    />
                  )}
                </div>

                {/* Children: ancestor chain from domain root → selected member */}
                <div className="dimension-tree__children">
                  {ancestors.map((anc, idx) => {
                    const ancFlat = anc.labels as Record<string, string | undefined> | undefined;
                    const ancLabel = ancFlat?.[tagLanguage] ?? ancFlat?.en ?? ancFlat?.fallback ?? anc.qname;
                    return (
                      <div key={`${anc.conceptId ?? anc.qname}-${idx}`} className="dimension-tree__node dimension-tree__node--nested">
                        <div className="dimension-tree__row">
                          <span className="dimension-tree__icon">
                            <i className="fa-regular fa-circle" />
                          </span>
                          <Typography variant="body-5" style={{ flexGrow: 1 }}>
                            {ancLabel}
                          </Typography>
                        </div>
                      </div>
                    );
                  })}
                  {/* Selected member (leaf) */}
                  <div className="dimension-tree__node dimension-tree__node--nested">
                    <div className="dimension-tree__row">
                      <span className="dimension-tree__icon">
                        <i className="fa-solid fa-circle-dot" />
                      </span>
                      <Typography variant="body-5" style={{ flexGrow: 1 }}>
                        {memberLabel}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {!readOnly && (
          <button className="show-more-btn" onClick={() => setIsAddModalOpen(true)} disabled={busy}>
            <i className="fa fa-plus" />
            <Typography variant="body-5" fontWeight="bold" color="#547ba2">
              {i18n('tag.dimensions.add')}
            </Typography>
          </button>
        )}
      </LabeledSection>

      <DimensionsModal
        conceptName={conceptName}
        taxonomyId={tag.taxonomyId}
        show={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        onConfirm={handleAdd}
        onTypedValueAdded={handleTypedValueAdded}
      />
    </>
  );
};

function collectMembers(node: AvailableMember, out: Map<string, { labels: any; type: 'dimension' | 'member'; qname: string }>) {
  if (node.conceptId) out.set(node.conceptId, { labels: node.labels, type: 'member', qname: node.id });
  for (const child of node.children) collectMembers(child, out);
}

function findMemberAncestors(
  response: { hypercubes: Array<{ dimensions: AvailableDimension[] }> },
  dimensionConceptId: string,
  memberConceptId: string
): Array<{ conceptId: string | null; qname: string; labels: any }> {
  for (const hc of response.hypercubes) {
    const dim = hc.dimensions.find((d) => d.conceptId === dimensionConceptId);
    if (!dim) continue;
    for (const domain of dim.domains) {
      const path = findPathToMember(domain.members, memberConceptId, []);
      if (path) {
        // Drop the trailing node (the selected member — UI renders it separately)
        return path.slice(0, -1).map((n) => ({ conceptId: n.conceptId, qname: n.id, labels: n.labels }));
      }
    }
  }
  return [];
}

function findPathToMember(node: AvailableMember, targetConceptId: string, acc: AvailableMember[]): AvailableMember[] | null {
  const next = [...acc, node];
  if (node.conceptId === targetConceptId) return next;
  for (const child of node.children) {
    const hit = findPathToMember(child, targetConceptId, next);
    if (hit) return hit;
  }
  return null;
}
